import { API_BASE } from '../config/constants';
import { getFreshIdToken } from './auth';

const appendAdminDateFilterParams = (q, {
  dateMode = 'all',
  dateYear = '',
  dateMonth = '',
  dateDay = '',
} = {}) => {
  q.set('dateMode', dateMode || 'all');
  if (dateYear) q.set('dateYear', dateYear);
  if (dateMonth) q.set('dateMonth', dateMonth);
  if (dateDay) q.set('dateDay', dateDay);
};

const apiClient = {
  async request(endpoint, options = {}) {
    const url = `${API_BASE}${endpoint}`;

    const token = await getFreshIdToken();

    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers
      },
      ...options
    };

    if (config.body && typeof config.body === 'object') {
      config.body = JSON.stringify(config.body);
    }

    try {
      console.log(`[API] ${options.method || 'GET'} ${endpoint}`);
      const response = await fetch(url, config);

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('[API] Non-JSON response:', text.substring(0, 200));
        throw new Error(`Server returned ${response.status}: ${text.substring(0, 100)}`);
      }

      const data = await response.json();

      if (!response.ok) {
        const isAuthError = response.status === 401 || response.status === 403;
        if (!isAuthError || token) {
          console.error('[API] Error response:', data);
        }
        if (
          response.status === 403
          && String(data.message || '').toLowerCase().includes('suspended')
        ) {
          window.dispatchEvent(new CustomEvent('coown-account-suspended', {
            detail: { message: data.message },
          }));
        }
        if (response.status === 403 && data.code === 'EMAIL_NOT_VERIFIED') {
          window.dispatchEvent(new CustomEvent('coown-email-not-verified', {
            detail: { message: data.message },
          }));
        }
        const err = new Error(data.message || `Request failed with status ${response.status}`);
        err.status = response.status;
        err.code = data.code;
        throw err;
      }

      return data;
    } catch (error) {
      if (error.message.includes('Failed to fetch')) {
        throw new Error('Network error: Cannot connect to server. Please check your connection.');
      }
      throw error;
    }
  },

  // --- Auth (login, profile, admin PIN) ---
  login: (credentials) => apiClient.request('/auth/login', { method: 'POST', body: credentials }),
  register: (data) => apiClient.request('/auth/register', { method: 'POST', body: data }),
  googleSignIn: (idToken, profile) => apiClient.request('/auth/google', { method: 'POST', body: { idToken, ...profile } }),
  getMe: () => apiClient.request('/auth/me'),
  updateProfile: (updates) => apiClient.request('/auth/profile', { method: 'PATCH', body: updates }),

  uploadProfilePhoto: async (file) => {
    const formData = new FormData();
    formData.append('photo', file);
    const token = await getFreshIdToken();
    const response = await fetch(`${API_BASE}/auth/profile-photo`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    const contentType = response.headers.get('content-type');
    const data = contentType?.includes('application/json') ? await response.json() : {};
    if (!response.ok) {
      const nested = data.error?.message || data.errors?.[0]?.message;
      throw new Error(data.message || nested || `Upload failed (${response.status})`);
    }
    return data;
  },

  // --- Properties (public listings on the site) ---
  getProperties: (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    return apiClient.request(`/properties?${params}`);
  },
  getProperty: (id) => apiClient.request(`/properties/${id}`),
  createProperty: (data) => apiClient.request('/properties', { method: 'POST', body: data }),
  updateProperty: (id, data) => apiClient.request(`/properties/${id}`, { method: 'PATCH', body: data }),
  deleteProperty: (id) => apiClient.request(`/properties/${id}`, { method: 'DELETE' }),

  listPropertyDocuments: (propertyId) => apiClient.request(`/properties/${encodeURIComponent(propertyId)}/documents`),
  uploadPropertyDocument: async (propertyId, file, title = '') => {
    const formData = new FormData();
    formData.append('file', file);
    if (title) formData.append('title', title);
    const token = await getFreshIdToken();
    const response = await fetch(`${API_BASE}/properties/${encodeURIComponent(propertyId)}/documents`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData
    });
    const contentType = response.headers.get('content-type');
    const data = contentType?.includes('application/json') ? await response.json() : {};
    if (!response.ok) {
      throw new Error(data.message || `Upload failed (${response.status})`);
    }
    return data;
  },
  registerPropertyDocument: (propertyId, body) =>
    apiClient.request(`/properties/${encodeURIComponent(propertyId)}/documents`, {
      method: 'POST',
      body
    }),
  deletePropertyDocument: (propertyId, docId) =>
    apiClient.request(`/properties/${encodeURIComponent(propertyId)}/documents/${encodeURIComponent(docId)}`, {
      method: 'DELETE'
    }),
  reviewPropertyDocument: (propertyId, docId, status) =>
    apiClient.request(`/properties/${encodeURIComponent(propertyId)}/documents/${encodeURIComponent(docId)}/review`, {
      method: 'PATCH',
      body: { status }
    }),
  downloadPropertyDocument: (propertyId, docId) =>
    apiClient.request(`/properties/${encodeURIComponent(propertyId)}/documents/${encodeURIComponent(docId)}/download`),
  requestPropertyDocumentAccess: (propertyId, message = '') =>
    apiClient.request(`/properties/${encodeURIComponent(propertyId)}/documents/request-access`, {
      method: 'POST',
      body: { message }
    }),
  getPropertyDocumentQueueStats: () => apiClient.request('/admin/property-documents/stats'),
  listPendingPropertyDocuments: () => apiClient.request('/admin/property-documents/pending'),
  listPropertyDocumentsForAdmin: (status = 'all') =>
    apiClient.request(`/admin/property-documents?status=${encodeURIComponent(status)}`),
  listPropertyDocumentAccessRequests: (status = 'pending') =>
    apiClient.request(`/admin/property-document-access?status=${encodeURIComponent(status)}`),
  listGrantedPropertyDocumentAccess: () => apiClient.request('/admin/property-document-access/grants'),
  listRevokedPropertyDocumentAccess: () => apiClient.request('/admin/property-document-access/revoked'),
  regrantPropertyDocumentAccess: (id) =>
    apiClient.request(`/admin/property-document-access/revoked/${encodeURIComponent(id)}/regrant`, {
      method: 'POST'
    }),
  revokePropertyDocumentAccess: (id) =>
    apiClient.request(`/admin/property-document-access/grants/${encodeURIComponent(id)}`, {
      method: 'DELETE'
    }),
  reviewPropertyDocumentAccess: (id, decision) =>
    apiClient.request(`/admin/property-document-access/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: { decision }
    }),

  // Investments
  getInvestments: () => apiClient.request('/investments'),
  getInvestmentQuote: (propertyId, shares) =>
    apiClient.request(`/investments/quote?propertyId=${encodeURIComponent(propertyId)}&shares=${encodeURIComponent(shares)}`),
  invest: (propertyId, shares, settlementRail = 'fiat') =>
    apiClient.request('/investments', {
      method: 'POST',
      body: { propertyId, shares, settlementRail }
    }),
  sellShares: (investmentId, shares) => apiClient.request('/investments/sell', { method: 'POST', body: { investmentId, shares } }),

  getTokenizationConfig: () => apiClient.request('/tokenization/config'),
  getMyAlgorandWallet: () => apiClient.request('/tokenization/wallet'),
  getPropertyTokenization: (propertyId) =>
    apiClient.request(`/tokenization/properties/${encodeURIComponent(propertyId)}`),

  getCryptoCheckoutConfig: () => apiClient.request('/crypto-checkout/config'),
  createCryptoCheckoutQuote: (propertyId, shares, payAsset = 'USDCa') =>
    apiClient.request('/crypto-checkout/quote', {
      method: 'POST',
      body: { propertyId, shares, payAsset }
    }),
  executeCryptoCheckout: (sessionId) =>
    apiClient.request('/crypto-checkout/execute', {
      method: 'POST',
      body: { sessionId }
    }),
  recoverPendingCryptoCheckouts: () =>
    apiClient.request('/crypto-checkout/recover-pending', { method: 'POST' }),

  getMarketplaceListings: () => apiClient.request('/marketplace/listings'),
  buyMarketplaceListing: (listingId, shares = 1) =>
    apiClient.request('/marketplace/buy', { method: 'POST', body: { listingId, shares } }),

  listDistributionRuns: (params = {}) => {
    const q = new URLSearchParams();
    if (params.propertyId) q.set('propertyId', params.propertyId);
    if (params.status) q.set('status', params.status);
    const qs = q.toString();
    return apiClient.request(`/distributions${qs ? `?${qs}` : ''}`);
  },
  getDistributionRun: (runId) => apiClient.request(`/distributions/${runId}`),
  createDistributionRun: (body) =>
    apiClient.request('/distributions', { method: 'POST', body }),
  approveDistributionRun: (runId) =>
    apiClient.request(`/distributions/${runId}/approve`, { method: 'POST' }),
  processDistributionRun: (runId, body = {}) =>
    apiClient.request(`/distributions/${runId}/process`, { method: 'POST', body }),
  getMyDistributions: () => apiClient.request('/distributions/mine'),

  // FX & wallet (USD ledger)
  getFxRates: () => apiClient.request('/fx/rates'),
  getWallet: () => apiClient.request('/wallet'),
  depositFunds: (amountUsd, mode = 'test') =>
    apiClient.request('/wallet/deposit', { method: 'POST', body: { amountUsd, mode } }),

  getFeeStatus: () => apiClient.request('/fees/status'),
  getStripeConfig: () => apiClient.request('/payments/config'),
  createStripeSetupIntent: () => apiClient.request('/payments/setup-intent', { method: 'POST' }),
  saveStripePaymentMethod: (paymentMethodId) =>
    apiClient.request('/payments/payment-method', { method: 'POST', body: { paymentMethodId } }),
  createStripeDepositIntent: (amountUsd) =>
    apiClient.request('/payments/deposit-intent', { method: 'POST', body: { amountUsd } }),

  // Favorites
  getFavorites: () => apiClient.request('/favorites'),
  addFavorite: (propertyId) => apiClient.request(`/favorites/${propertyId}`, { method: 'POST' }),
  removeFavorite: (propertyId) => apiClient.request(`/favorites/${propertyId}`, { method: 'DELETE' }),

  // KYC
  submitKYC: (data) => apiClient.request('/kyc/submit', { method: 'POST', body: data }),
  getMyKYC: () => apiClient.request('/kyc/my'),
  getPendingKYC: () => apiClient.request('/kyc/pending'),
  listKYCForAdmin: ({
    status = 'all',
    search = '',
    page = 1,
    limit = 10,
    dateMode = 'all',
    dateYear = '',
    dateMonth = '',
    dateDay = '',
  } = {}) => {
    const q = new URLSearchParams({
      status,
      search,
      page: String(page),
      limit: String(limit),
    });
    appendAdminDateFilterParams(q, { dateMode, dateYear, dateMonth, dateDay });
    return apiClient.request(`/kyc/admin/list?${q}`);
  },
  getKYCById: (id) => apiClient.request(`/kyc/admin/${encodeURIComponent(id)}`),
  downloadKYCExport: async ({ status = 'all', search = '' } = {}) => {
    const token = await getFreshIdToken();
    const q = new URLSearchParams({ status, search });
    const response = await fetch(`${API_BASE}/kyc/admin/export?${q}`, {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
    if (!response.ok) {
      let message = 'CSV export failed';
      try {
        const data = await response.json();
        message = data.message || message;
      } catch (_) {
        /* non-json error body */
      }
      throw new Error(message);
    }
    const blob = await response.blob();
    const stamp = new Date().toISOString().slice(0, 10);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `coown-kyc-users-${status}-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
  reviewKYC: (id, status, notes) =>
    apiClient.request(`/kyc/${encodeURIComponent(id)}/review`, { method: 'PATCH', body: { status, notes } }),

  getPlatformActivity: () => apiClient.request('/admin/platform-activity'),
  listConversationReportsForAdmin: ({
    status = 'open',
    search = '',
    page = 1,
    limit = 10,
    dateRange = 'all',
    dateMode = 'all',
    dateYear = '',
    dateMonth = '',
    dateDay = '',
  } = {}) => {
    const q = new URLSearchParams({
      status,
      search,
      page: String(page),
      limit: String(limit),
      dateRange: dateMode && dateMode !== 'all' ? 'all' : dateRange,
    });
    appendAdminDateFilterParams(q, { dateMode, dateYear, dateMonth, dateDay });
    return apiClient.request(`/admin/conversation-reports?${q}`);
  },
  downloadConversationReportsExport: async () => {
    const token = await getFreshIdToken();
    const response = await fetch(`${API_BASE}/admin/conversation-reports/export`, {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
    if (!response.ok) {
      let message = 'CSV export failed';
      try {
        const data = await response.json();
        message = data.message || message;
      } catch (_) {
        /* non-json error body */
      }
      throw new Error(message);
    }
    const blob = await response.blob();
    const stamp = new Date().toISOString().slice(0, 10);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `coown-chat-reports-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
  getConversationReportForAdmin: (id) =>
    apiClient.request(`/admin/conversation-reports/${encodeURIComponent(id)}`),
  updateConversationReportForAdmin: (id, { status, notes = '', resolutionType = '', blockReportedUser = false }) =>
    apiClient.request(`/admin/conversation-reports/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: { status, notes, resolutionType, blockReportedUser },
    }),
  resendConversationReportEmail: (id) =>
    apiClient.request(`/admin/conversation-reports/${encodeURIComponent(id)}/resend-reporter-email`, {
      method: 'POST',
    }),
  listUsersForAdmin: ({
    search = '',
    page = 1,
    limit = 10,
    kycFilter = 'all',
    dateMode = 'all',
    dateYear = '',
    dateMonth = '',
    dateDay = '',
  } = {}) => {
    const q = new URLSearchParams({
      search,
      page: String(page),
      limit: String(limit),
      kycFilter,
    });
    appendAdminDateFilterParams(q, { dateMode, dateYear, dateMonth, dateDay });
    return apiClient.request(`/admin/users?${q}`);
  },
  getUserForAdmin: (uid) => apiClient.request(`/admin/users/${encodeURIComponent(uid)}`),
  updateUserEmailForAdmin: (uid, email) =>
    apiClient.request(`/admin/users/${encodeURIComponent(uid)}/email`, {
      method: 'PATCH',
      body: { email },
    }),
  downloadUsersExport: async ({ search = '' } = {}) => {
    const token = await getFreshIdToken();
    const q = new URLSearchParams({ search });
    const response = await fetch(`${API_BASE}/admin/users/export?${q}`, {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
    if (!response.ok) {
      let message = 'CSV export failed';
      try {
        const data = await response.json();
        message = data.message || message;
      } catch (_) {
        /* non-json error body */
      }
      throw new Error(message);
    }
    const blob = await response.blob();
    const stamp = new Date().toISOString().slice(0, 10);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `coown-registered-users-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },

  // --- Submissions (lister submits → admin approves → publish to site) ---
  createSubmission: (data) => apiClient.request('/submissions', { method: 'POST', body: data }),
  getSubmissions: () => apiClient.request('/submissions'),
  getDeletedSubmissions: () => apiClient.request('/submissions/deleted'),
  restoreSubmission: (id) => apiClient.request(`/submissions/${id}/restore`, { method: 'POST' }),
  lookupUserByEmail: (email) =>
    apiClient.request(`/auth/users/lookup?email=${encodeURIComponent(email)}`),
  blockPlatformUser: (uid, reason = '') =>
    apiClient.request(`/auth/users/${encodeURIComponent(uid)}/block`, { method: 'POST', body: { reason } }),
  unblockPlatformUser: (uid) =>
    apiClient.request(`/auth/users/${encodeURIComponent(uid)}/unblock`, { method: 'POST' }),
  getMySubmissions: () => apiClient.request('/submissions/mine'),
  getMyDeletedSubmissions: () => apiClient.request('/submissions/mine/deleted'),
  updateSubmission: (id, data) => apiClient.request(`/submissions/${id}`, { method: 'PATCH', body: data }),
  publishSubmission: (id) => apiClient.request(`/submissions/${id}/publish`, { method: 'POST' }),
  unpublishSubmission: (id) => apiClient.request(`/submissions/${id}/unpublish`, { method: 'POST' }),
  deleteSubmission: (id) => apiClient.request(`/submissions/${id}`, { method: 'DELETE' }),
  setAdminPin: (pin) => apiClient.request('/auth/admin-pin', { method: 'POST', body: { pin } }),
  changeAdminPin: (currentPin, newPin) => apiClient.request('/auth/admin-pin/change', {
    method: 'POST',
    body: { currentPin, newPin }
  }),

  getMessagesUnreadCount: () => apiClient.request('/messages/unread-count'),
  listListingConversations: () => apiClient.request('/messages'),
  getPropertyListingChat: (propertyId) =>
    apiClient.request(`/messages/property/${encodeURIComponent(propertyId)}/chat`),
  sendPropertyListingMessage: (propertyId, text) =>
    apiClient.request(`/messages/property/${encodeURIComponent(propertyId)}/messages`, {
      method: 'POST',
      body: { text }
    }),
  getListingMessages: (conversationId, { markRead = true } = {}) => {
    const query = markRead ? '' : '?markRead=false';
    return apiClient.request(`/messages/${encodeURIComponent(conversationId)}/messages${query}`);
  },
  sendListingMessage: (conversationId, text) =>
    apiClient.request(`/messages/${encodeURIComponent(conversationId)}/messages`, {
      method: 'POST',
      body: { text }
    }),
  reportListingConversation: (conversationId, { category, reason }) =>
    apiClient.request(`/messages/${encodeURIComponent(conversationId)}/report`, {
      method: 'POST',
      body: { category, reason }
    }),

  getInsights: (params = {}) => {
    const q = new URLSearchParams();
    if (params.search) q.set('search', params.search);
    if (params.tag) q.set('tag', params.tag);
    q.set('page', String(params.page || 1));
    q.set('pageSize', String(params.pageSize || 12));
    const qs = q.toString();
    return apiClient.request(`/insights?${qs}`);
  },
  getInsight: (id) => apiClient.request(`/insights/${encodeURIComponent(id)}`),
  getAdminInsights: (search = '') =>
    apiClient.request(`/insights/admin/list${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  createInsight: (data) => apiClient.request('/insights', { method: 'POST', body: data }),
  updateInsight: (id, data) => apiClient.request(`/insights/${encodeURIComponent(id)}`, { method: 'PATCH', body: data }),
  publishInsight: (id) => apiClient.request(`/insights/${encodeURIComponent(id)}/publish`, { method: 'POST' }),
  unpublishInsight: (id) => apiClient.request(`/insights/${encodeURIComponent(id)}/unpublish`, { method: 'POST' }),
  deleteInsight: (id) => apiClient.request(`/insights/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  seedDemoInsights: () => apiClient.request('/insights/admin/seed-demo', { method: 'POST' }),

  getLegalPage: (slug) => apiClient.request(`/legal-pages/${encodeURIComponent(slug)}`),
  getAdminLegalPages: () => apiClient.request('/legal-pages/admin/list'),
  getAdminLegalPage: (slug) => apiClient.request(`/legal-pages/admin/${encodeURIComponent(slug)}`),
  updateLegalPage: (slug, data) => apiClient.request(`/legal-pages/${encodeURIComponent(slug)}`, { method: 'PATCH', body: data }),
  publishLegalPage: (slug) => apiClient.request(`/legal-pages/${encodeURIComponent(slug)}/publish`, { method: 'POST' }),
  unpublishLegalPage: (slug) => apiClient.request(`/legal-pages/${encodeURIComponent(slug)}/unpublish`, { method: 'POST' }),
  seedDefaultLegalPages: (overwrite = false) =>
    apiClient.request('/legal-pages/admin/seed-defaults', { method: 'POST', body: { overwrite } }),

  uploadCoverImage: async (file, folder = 'insights') => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('folder', folder);
    const token = await getFreshIdToken();
    const response = await fetch(`${API_BASE}/upload/image`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData
    });
    const contentType = response.headers.get('content-type');
    const data = contentType?.includes('application/json') ? await response.json() : {};
    if (!response.ok) {
      const nested = data.error?.message || data.errors?.[0]?.message;
      throw new Error(data.message || nested || `Upload failed (${response.status})`);
    }
    return data;
  },

  uploadSubmissionImages: async (files) => {
    const formData = new FormData();
    files.forEach((f) => formData.append('images', f));
    const token = await getFreshIdToken();
    const response = await fetch(`${API_BASE}/submissions/upload-images`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData
    });
    const contentType = response.headers.get('content-type');
    const data = contentType?.includes('application/json') ? await response.json() : {};
    if (!response.ok) {
      const nested = data.error?.message || data.errors?.[0]?.message;
      throw new Error(data.message || nested || `Upload failed (${response.status})`);
    }
    return data;
  },

  uploadSubmissionVideo: async (file) => {
    const formData = new FormData();
    formData.append('video', file);
    const token = await getFreshIdToken();
    const response = await fetch(`${API_BASE}/submissions/upload-video`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData
    });
    const contentType = response.headers.get('content-type');
    const data = contentType?.includes('application/json') ? await response.json() : {};
    if (!response.ok) {
      const nested = data.error?.message || data.errors?.[0]?.message;
      throw new Error(data.message || nested || `Upload failed (${response.status})`);
    }
    return data;
  },

  // Upload
  getSignedUrl: (filename, contentType) => apiClient.request('/upload/signed-url', { method: 'POST', body: { filename, contentType } }),
};

export default apiClient;
